import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ReportDisplay from './components/ReportDisplay';
import { useBirthInfo } from '../../contexts/BirthInfoContext';
import SEOSchema from '../../components/feature/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const liveReportSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${siteUrl}/live-report`,
  name: '라이브 운세 리포트 — AI 실시간 운세 분석',
  description: '생년월일 정보를 입력하면 AI가 실시간으로 생성하는 맞춤형 운세 분석 리포트. 사주팔자와 오행을 기반으로 오늘의 운세를 즉시 제공합니다.',
  url: `${siteUrl}/live-report`,
  provider: {
    '@type': 'Organization',
    name: 'DESTINY GPT',
    url: siteUrl,
  },
  serviceType: '실시간 운세 리포트',
  areaServed: { '@type': 'Country', name: '대한민국' },
  inLanguage: 'ko-KR',
};

export default function LiveReportPage() {
  const navigate = useNavigate();
  const { birthInfo } = useBirthInfo();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (birthInfo && !hasStarted) {
      setHasStarted(true);
      setIsGenerating(true);
      setTimeout(() => {
        setIsGenerating(false);
      }, 2500);
    }
  }, [birthInfo, hasStarted]);

  if (!birthInfo) {
    return (
      <div className="min-h-screen bg-cosmic-gradient">
        <SEOSchema schema={liveReportSchema} id="live-report-schema" />
        <Navbar />
        <main className="pt-16 sm:pt-20 min-h-screen flex items-center justify-center px-4">
          <div className="text-center px-4">
            <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-primary-purple to-primary-light flex items-center justify-center">
              <i className="ri-information-line text-white text-3xl sm:text-5xl"></i>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
              생년월일 정보가 필요합니다
            </h2>
            <p className="text-primary-light mb-6 sm:mb-8 text-sm sm:text-base">
              라이브 리포트를 생성하려면 먼저 생년월일 정보를 입력해주세요.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 sm:px-8 py-3 bg-gradient-to-r from-primary-gold to-primary-gold-light rounded-full text-primary-dark font-bold hover:shadow-[0_0_20px_rgba(255,215,0,0.5)] transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              정보 입력하러 가기
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      <SEOSchema schema={liveReportSchema} id="live-report-schema" />
      <Navbar />
      <main className="pt-16 sm:pt-20">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-8 sm:py-12">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-3 sm:mb-4">
              라이브 운세 리포트
            </h1>
            <p className="text-base sm:text-xl text-primary-light">
              실시간으로 생성되는 당신만의 운세 분석
            </p>
          </div>

          {isGenerating ? (
            <div className="max-w-2xl mx-auto text-center py-16 sm:py-20 px-4">
              <div className="w-20 h-20 sm:w-32 sm:h-32 mx-auto mb-6 sm:mb-8 relative">
                <div className="absolute inset-0 rounded-full border-4 border-primary-gold/30"></div>
                <div className="absolute inset-0 rounded-full border-4 border-primary-gold border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-magic-fill text-primary-gold text-3xl sm:text-5xl"></i>
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                운세 리포트를 생성하고 있습니다...
              </h3>
              <p className="text-primary-light text-sm sm:text-base">
                AI가 당신의 운명을 분석하고 있습니다. 잠시만 기다려주세요.
              </p>
            </div>
          ) : (
            <ReportDisplay />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
